import React, { useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { htmlLinter, cssLinter, lintGutter } from '../../utils/htmlLinter';
import config from "../../config";
import QuestionEditor from './QuestionEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faChevronDown, faChevronUp, faPlus, faMagic } from '@fortawesome/free-solid-svg-icons';
import CONSTANTS from '../../constants/contentCreationConstants';
import { Modal } from 'react-bootstrap';
import { apiPost, apiPostFormData, apiDelete, apiPut, apiPutFormData, apiGet, apiPostWithBaseURL, apiGetWithBaseURL } from '../../utils/apiAuth';
import TestCaseEditModal from './TestCaseEditModal';
import { useToast } from '../Toast';
const HTMLForm = ({
  selectedQuestion,
  setSelectedQuestion,
  pageName,
  setPageName,
  files,
  setFiles,
  loadedTestCases,
  loadedSelectedTestCases,
  loadedDefaultTemplate,
  jsData: propJSData,
  onJSDataChange,
  imagePath,
  videoPath,
  questionId,
  onGenerateTestCases,
  onFileModified,
  onTestCasesGenerated,
  onDefaultTemplateChange,
  onImageUploaded,
  onVideoUploaded,
  resetTrigger,
  isQuestionLoading,
  onImageUrlsValidation,
  onImageUrlsChange,
  loadedImageUrls
}) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('index.html');
  const [isGeneratingTestCases, setIsGeneratingTestCases] = useState(false);
  const [editingFileName, setEditingFileName] = useState(null);
  const [editingFileNameValue, setEditingFileNameValue] = useState('');
  const [generatedTestCases, setGeneratedTestCases] = useState({});
  const [selectedTestCases, setSelectedTestCases] = useState({});
  const [isLoadingFromAPI, setIsLoadingFromAPI] = useState(false);
  const [defaultTemplateContent, setDefaultTemplateContent] = useState('');
  const [hasDefaultTemplate, setHasDefaultTemplate] = useState(false);
  const lastEmittedPayloadRef = React.useRef(null);
  const prevQuestionIdRef = React.useRef(questionId);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const [videoCacheBust, setVideoCacheBust] = useState(0);
  const withCacheBust = (url, bust) => {
    if (!url) return '';
    if (!bust) return url;
    return url.includes('?') ? `${url}&t=${bust}` : `${url}?t=${bust}`;
  };
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [showJSForm, setShowJSForm] = useState(false);
  const [isQuestionSaved, setIsQuestionSaved] = useState(() => {
    // Initialize based on sessionStorage
    const questionId = sessionStorage.getItem('currentQuestionId') || '';
    return questionId.length > 0;
  });
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'image' or 'video'
  const [showJSTestCaseErrorModal, setShowJSTestCaseErrorModal] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [showImageForm, setShowImageForm] = useState(false);
  const refreshTimeoutRef = React.useRef(null);
  const [iframeKey, setIframeKey] = React.useState(0);
  const iframeRef = React.useRef(null);
  const [isGeneratingJSTestcases, setIsGeneratingJSTestcases] = React.useState(false);
  const [jsValidationResults, setJsValidationResults] = React.useState([]);
  const [isValidating, setIsValidating] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editingTestCase, setEditingTestCase] = React.useState(null);
  const [editingTestCaseIndex, setEditingTestCaseIndex] = React.useState(null);
  const [isCloning, setIsCloning] = React.useState(false);

  // Force refresh iframe by updating key
  const forceRefreshIframe = React.useCallback(() => {
    setIframeKey(prev => prev + 1);
  }, []);


  // Handle JS form data changes
  const handleJSDataChange = (newJSData) => {
    // Store data for the specific JS file
    const currentFileName = getCurrentFileData?.name;
    if (currentFileName && getCurrentFileData?.type === 'JS') {
      const updatedJSData = {
        ...propJSData,
        [currentFileName]: newJSData
      };
      
      // Pass the updated data up to the parent component
      if (onJSDataChange) {
        onJSDataChange(updatedJSData);
      }
    }
  };


  const [previewUrl, setPreviewUrl] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState(CONSTANTS.DEFAULT_PREVIEW_TAB); // 'output', 'console', or 'upload'
  const [consoleLogs, setConsoleLogs] = useState([]);

  // Initialize default files if none exist
  React.useEffect(() => {
    if (files.length === 0) {
      setFiles([
        {
          id: 'index.html',
          name: 'index.html',
          type: 'HTML',
          content: ''
        }
      ]);
    }
  }, [files.length, setFiles]);

  // Clear sessionStorage on component mount (page refresh)
  React.useEffect(() => {
    // Clear currentQuestionId from sessionStorage on refresh
    sessionStorage.removeItem('currentQuestionId');
    
    // Set initial state for new question
    setIsQuestionSaved(false);
    setUploadedImage(null);
    setUploadedVideo(null);
  }, []);

  // Load test cases from parent component when loadedTestCases prop changes
  React.useEffect(() => {
    // Check if this is a question change using ref (before updating it)
    const isQuestionChange = prevQuestionIdRef.current !== undefined && 
                             prevQuestionIdRef.current !== questionId && 
                             questionId;
    
    if (isQuestionChange && (!loadedTestCases || Object.keys(loadedTestCases).length === 0)) {
      // Question changed but no loadedTestCases yet - clear existing test cases
      setGeneratedTestCases({});
      setSelectedTestCases({});
      setJsValidationResults([]);
      // Update the ref after clearing
      prevQuestionIdRef.current = questionId;
      return;
    }
    
    if (loadedTestCases && Object.keys(loadedTestCases).length > 0) {
      let hasValidData = false;
      Object.keys(loadedTestCases).forEach(fileName => {
        const testCaseData = loadedTestCases[fileName];
        const hasNew = Array.isArray(testCaseData?.test_cases) && testCaseData.test_cases.length > 0;
        const hasOld = Array.isArray(testCaseData?.structure) && testCaseData.structure.length > 0;
        if (hasNew || hasOld) {
          hasValidData = true;
        }
      });
      
      if (hasValidData) {
        setIsLoadingFromAPI(true);
        
        // Always load new test cases when loadedTestCases changes
        // Extract just the structure array from each file's test case data
        const extractedTestCases = {};
        Object.keys(loadedTestCases).forEach(fileName => {
          const testCaseData = loadedTestCases[fileName];
          let cases = testCaseData.test_cases || testCaseData.structure || [];
          // Normalize legacy bundled CSS rules into one test case per property
          if (
            typeof fileName === 'string' &&
            fileName.toLowerCase().endsWith('.css') &&
            Array.isArray(cases)
          ) {
            cases = expandCssTestCasesToPerProperty(cases);
          }
          extractedTestCases[fileName] = cases;
        });

        // Merge without shrinking existing arrays to avoid losing items on checkbox updates
        setGeneratedTestCases(prev => {
          // Check if this is a question change (using the previous value before ref update)
          if (isQuestionChange) {
            // Question change - replace all
            return extractedTestCases;
          }
          
          // Same question - use merging logic
          const merged = { ...prev };
          Object.keys(extractedTestCases).forEach(file => {
            const incoming = extractedTestCases[file] || [];
            const existing = prev[file] || [];
            // If we already have test cases and incoming is smaller, keep existing
            if (existing.length > 0 && incoming.length < existing.length) {
              merged[file] = existing;
            } else {
              merged[file] = incoming;
            }
          });
          return merged;
        });
        
        // Reset the flag after a short delay to allow the effect to complete
        setTimeout(() => setIsLoadingFromAPI(false), 100);
      } else {
        // No valid data found; treat as new question state
        if (isQuestionChange) {
          setGeneratedTestCases({});
          setSelectedTestCases({});
          setJsValidationResults([]);
        }
      }
    }
    
    if (loadedSelectedTestCases && Object.keys(loadedSelectedTestCases).length > 0) {
      // Guard: only set if different to avoid loops
      setSelectedTestCases(prev => {
        const prevStr = JSON.stringify(prev || {});
        const nextStr = JSON.stringify(loadedSelectedTestCases || {});
        if (prevStr === nextStr) return prev;
        return loadedSelectedTestCases;
      });
    }
    
    // Update the ref at the end for next comparison
    prevQuestionIdRef.current = questionId;
    
  }, [loadedTestCases, loadedSelectedTestCases, questionId]);

  // Simple useEffect to watch sessionStorage changes
  React.useEffect(() => {
    const checkSessionStorage = () => {
      const questionId = sessionStorage.getItem('currentQuestionId') || '';
      const isSaved = questionId.length > 0;
      
      setIsQuestionSaved(isSaved);
      
      // If no question is saved, reset upload states
      if (!isSaved) {
        setUploadedImage(null);
        setUploadedVideo(null);
      }
    };

    // Check every 100ms for changes
    const interval = setInterval(checkSessionStorage, 100);

    return () => clearInterval(interval);
  }, []);

  // Load existing uploaded files when question data is loaded
  React.useEffect(() => {
    if (imagePath) {
      setUploadedImage({
        url: imagePath,
        filename: imagePath.split('/').pop(),
        type: 'image'
      });
    } else {
      setUploadedImage(null);
    }
    
    if (videoPath) {
      setUploadedVideo({
        url: videoPath,
        filename: videoPath.split('/').pop(),
        type: 'video'
      });
    } else {
      setUploadedVideo(null);
    }
  }, [imagePath, videoPath, questionId]);

  // Force refresh media display when question is loaded or reloaded
  React.useEffect(() => {
    if (questionId && (imagePath || videoPath)) {
      
      // Force state update to ensure media is displayed
      if (imagePath) {
        setUploadedImage({
          url: imagePath,
          filename: imagePath.split('/').pop(),
          type: 'image'
        });
      }
      
      if (videoPath) {
        setUploadedVideo({
          url: videoPath,
          filename: videoPath.split('/').pop(),
          type: 'video'
        });
      }
    }
  }, [questionId, imagePath, videoPath]);

  // Force refresh when question is saved and reloaded using sessionStorage
  React.useEffect(() => {
    const currentQuestionId = sessionStorage.getItem('currentQuestionId') || '';
    
    // If questionId matches currentQuestionId and we have media paths, refresh media
    if (currentQuestionId === questionId && (imagePath || videoPath)) {
      
      
      // Force refresh by updating state
      if (imagePath) {
        setUploadedImage({
          url: imagePath,
          filename: imagePath.split('/').pop(),
          type: 'image'
        });
      }
      
      if (videoPath) {
        setUploadedVideo({
          url: videoPath,
          filename: videoPath.split('/').pop(),
          type: 'video'
        });
      }
    }
  }, [questionId, imagePath, videoPath]);

  // Reset all data when creating new question (currentQuestionId is empty)
  React.useEffect(() => {
    const currentQuestionId = sessionStorage.getItem('currentQuestionId') || '';
    
    // If currentQuestionId is empty, reset all form data
    if (currentQuestionId === '') {
      
      // Clear all local state
      setGeneratedTestCases({});
      setSelectedTestCases({});
      setDefaultTemplateContent('');
      setHasDefaultTemplate(false);
      setActiveTab('index.html');
      setIsGeneratingTestCases(false);
      setEditingFileName(null);
      setEditingFileNameValue('');
      setPreviewUrl('');
      setShowPreviewModal(false);
      setUploadedImage(null);
      setUploadedVideo(null);
      setIsQuestionSaved(false);
      setActivePreviewTab(CONSTANTS.DEFAULT_PREVIEW_TAB);
      setIsFormCollapsed(false); // Reset form collapse state
      setImageUrls([]);
      setShowImageForm(false);
      
      // Reset files to default
      setFiles([
        {
          id: 'index.html',
          name: 'index.html',
          type: 'HTML',
          content: ''
        }
      ]);
      
      // Reset page name in parent component
      setPageName('');
    }
  }, [setFiles, setPageName, questionId]);

  // Load default template from parent component
  React.useEffect(() => {
    if (loadedDefaultTemplate && loadedDefaultTemplate.trim() !== '') {
      setDefaultTemplateContent(loadedDefaultTemplate);
      setHasDefaultTemplate(true);
    } else if (!loadedDefaultTemplate || loadedDefaultTemplate.trim() === '') {
      setDefaultTemplateContent('');
      setHasDefaultTemplate(false);
    }
  }, [loadedDefaultTemplate]);

  // Load image URLs from parent component
  React.useEffect(() => {
    if (loadedImageUrls && Array.isArray(loadedImageUrls) && loadedImageUrls.length > 0) {
      setImageUrls(loadedImageUrls);
    } else {
      // Clear when no loaded image URLs
      setImageUrls([]);
    }
  }, [loadedImageUrls]);

  // Set active tab to index.html only when question is being loaded (not when files change)
  React.useEffect(() => {
    if (isQuestionLoading && files.length > 0) {
      const indexHtmlFile = files.find(file => file.name === 'index.html' || file.id === 'index.html');
      if (indexHtmlFile) {
        setActiveTab('index.html');
      } else if (files.length > 0) {
        // If index.html doesn't exist, select the first file
        setActiveTab(files[0].name || files[0].id);
      }
    }
  }, [isQuestionLoading]);

  // Ensure we always have an active tab when files are available (only when no active tab is set)
  React.useEffect(() => {
    if (files.length > 0 && !activeTab) {
      const indexHtmlFile = files.find(file => file.name === 'index.html' || file.id === 'index.html');
      if (indexHtmlFile) {
        setActiveTab('index.html');
      } else {
        setActiveTab(files[0].name || files[0].id);
      }
    }
  }, [files.length, activeTab]);

  React.useEffect(() => {
    setJsValidationResults([]);
  }, [activeTab]);

  // Handle question change
  const handleQuestionChange = React.useCallback((value) => {
    setSelectedQuestion(value);
  }, [setSelectedQuestion]);

  // Seed Jodit once per question — do not pass live selectedQuestion as value (refreshes every keystroke)
  const [requirementsSeed, setRequirementsSeed] = React.useState(
    () => selectedQuestion || ""
  );
  React.useEffect(() => {
    setRequirementsSeed(selectedQuestion || "");
  }, [questionId]);
  React.useEffect(() => {
    if (!isQuestionLoading) {
      setRequirementsSeed(selectedQuestion || "");
    }
  }, [isQuestionLoading]); // hydrate after async question load completes


  // Handle reset trigger from parent component
  React.useEffect(() => {
    if (resetTrigger) {
      setGeneratedTestCases({});
      setSelectedTestCases({});
      
      // Only clear default template if it's not loaded from parent
      if (!loadedDefaultTemplate || loadedDefaultTemplate.trim() === '') {
        setDefaultTemplateContent('');
        setHasDefaultTemplate(false);
      }
      
      setActiveTab('index.html');
      setIsGeneratingTestCases(false);
      setEditingFileName(null);
      setEditingFileNameValue('');
      setPreviewUrl('');
      setShowPreviewModal(false);
      setUploadedImage(null);
      setUploadedVideo(null);
      setIsQuestionSaved(false);
      setActivePreviewTab(CONSTANTS.DEFAULT_PREVIEW_TAB);
      setIsFormCollapsed(false); // Reset form collapse state
      setImageUrls([]);
      setShowImageForm(false);
      
      // Reset files to default
      setFiles([
        {
          id: 'index.html',
          name: 'index.html',
          type: 'HTML',
          content: ''
        }
      ]);
      
      setPageName('');
    }
  }, [resetTrigger, loadedDefaultTemplate, setFiles, setPageName]);

  // Listen for currentQuestionId changes to reset form when switching to "Create New"
  React.useEffect(() => {
    const questionId = sessionStorage.getItem('currentQuestionId') || '';
    
    if (questionId === '') {
      // Clear all local state when questionId is empty (Create New)
      setGeneratedTestCases({});
      setSelectedTestCases({});
      setDefaultTemplateContent('');
      setHasDefaultTemplate(false);
      setActiveTab('index.html');
      setIsGeneratingTestCases(false);
      setEditingFileName(null);
      setEditingFileNameValue('');
      setPreviewUrl('');
      setShowPreviewModal(false);
      setUploadedImage(null);
      setUploadedVideo(null);
      setIsQuestionSaved(false);
      setActivePreviewTab(CONSTANTS.DEFAULT_PREVIEW_TAB);
      setIsFormCollapsed(false); // Reset form collapse state
      setImageUrls([]);
      setShowImageForm(false);
      
      // Reset files to default
      setFiles([
        {
          id: 'index.html',
          name: 'index.html',
          type: 'HTML',
          content: ''
        }
      ]);
      
      // Also reset pageName in parent component
      setPageName('');
    }
  }, [setFiles]); // Reset when questionId changes, not when selectedQuestion content changes

  const getCurrentFileData = React.useMemo(() => {
    if (activeTab === 'DefaultTemplate.html') {
      return {
        id: 'DefaultTemplate.html',
        name: 'DefaultTemplate.html',
        type: 'HTML',
        content: defaultTemplateContent
      };
    }
    // Try to find file by id first, then by name
    const foundFile = files.find(f => f.id === activeTab) || files.find(f => f.name === activeTab);
    return foundFile;
  }, [files, activeTab, defaultTemplateContent]);

  const getCurrentFileContent = React.useMemo(() => {
    return getCurrentFileData?.content || '';
  }, [getCurrentFileData]);



  // Auto-show JS form when JS file becomes active
  React.useEffect(() => {
    if (getCurrentFileData?.type === 'JS') {
      setShowJSForm(true);
    }
  }, [getCurrentFileData?.type]);

  // Combine regular files with default template for display
  const allFiles = React.useMemo(() => {
    const defaultTemplateFile = hasDefaultTemplate ? {
      id: 'DefaultTemplate.html',
      name: 'DefaultTemplate.html',
      type: 'HTML',
      content: defaultTemplateContent
    } : null;
    
    return defaultTemplateFile ? [...files, defaultTemplateFile] : files;
  }, [files, hasDefaultTemplate, defaultTemplateContent]);

  const handleDefaultTemplateChange = React.useCallback((content) => {
    setDefaultTemplateContent(content);
    if (onDefaultTemplateChange) {
      onDefaultTemplateChange(content);
    }
  }, [onDefaultTemplateChange]);

  const updateCurrentFileContent = React.useCallback((content) => {
    if (activeTab === 'DefaultTemplate.html') {
      handleDefaultTemplateChange(content);
    } else {
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === activeTab 
          ? { ...file, content }
          : file
      )
    );
    }
    
    // Mark file as modified when content changes
    if (onFileModified) {
      onFileModified(activeTab);
    }

    // If we're in console tab, refresh iframe to capture new console logs
    if (activePreviewTab === 'console') {
      // Clear old console logs when content changes
      setConsoleLogs([]);
      
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Force refresh by updating iframe key with debounce
      refreshTimeoutRef.current = setTimeout(() => {
        forceRefreshIframe();
      }, 500); // Longer delay for content changes
    }
  }, [activeTab, onFileModified, handleDefaultTemplateChange, activePreviewTab]);

  const getLanguage = React.useCallback((fileType) => {
    switch (fileType) {
      case 'HTML': return html();
      case 'CSS': return css();
      case 'JS': return javascript();
      default: return html();
    }
  }, []);

  const getLinter = React.useCallback((fileType) => {
    switch (fileType) {
      case 'HTML': return htmlLinter;
      case 'CSS': return cssLinter;
      default: return [];
    }
  }, []);

  const getFileExtension = React.useCallback((fileType) => {
    switch (fileType) {
      case 'HTML': return '.html';
      case 'CSS': return '.css';
      case 'JS': return '.js';
      default: return '.html';
    }
  }, []);

  const addNewFile = React.useCallback((type) => {
    // Check file limits
    const fileCount = files.filter(f => f.type === type).length;
    const maxFiles = 5;
    
    if (fileCount >= maxFiles) {
      toast.warning(`Maximum ${maxFiles} ${type} files allowed`);
      return;
    }
    
    const fileName = type === 'HTML' && fileCount === 0 ? 'index.html' : 
                   `${type.toLowerCase()}${fileCount + 1}${getFileExtension(type)}`;
    
    const newFile = {
      id: fileName,
      name: fileName,
      type: type,
      content: type === 'HTML' ? '' : ''
    };
    
    setFiles(prevFiles => [...prevFiles, newFile]);
    setActiveTab(fileName);
  }, [files, getFileExtension]);

  const addDefaultTemplate = React.useCallback(() => {
    if (hasDefaultTemplate) {
      toast.warning('Default template already exists');
      return;
    }
    setDefaultTemplateContent('');
    setHasDefaultTemplate(true);
    setActiveTab('DefaultTemplate.html');
  }, [hasDefaultTemplate]);

  const removeFile = React.useCallback((fileId) => {
    // Don't allow deletion of index.html
    if (fileId === 'index.html') return;
    
    // Handle DefaultTemplate.html removal
    if (fileId === 'DefaultTemplate.html') {
      setDefaultTemplateContent('');
      setHasDefaultTemplate(false);
      if (onDefaultTemplateChange) {
        onDefaultTemplateChange('');
      }
      // Switch to index.html if currently viewing DefaultTemplate.html
      if (activeTab === 'DefaultTemplate.html') {
        setActiveTab('index.html');
      }
      return;
    }
    
    if (files.length <= 1) return; // Don't remove the last file
    
    setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
    
    if (activeTab === fileId) {
      const remainingFiles = files.filter(f => f.id !== fileId);
      setActiveTab(remainingFiles[0].id);
    }
  }, [files, activeTab, onDefaultTemplateChange]);

  const startEditingFileName = React.useCallback((fileId, currentName) => {
    // Don't allow editing of index.html filename
    if (fileId === 'index.html') {
      toast.warning('Cannot edit index.html filename');
      return;
    }
    
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    
    // Extract filename without extension for editing
    const extension = getFileExtension(file.type);
    const nameWithoutExt = currentName.endsWith(extension) 
      ? currentName.slice(0, -extension.length) 
      : currentName;
    
    setEditingFileName(fileId);
    setEditingFileNameValue(nameWithoutExt);
  }, [files, getFileExtension]);

  const cancelEditingFileName = React.useCallback(() => {
    setEditingFileName(null);
    setEditingFileNameValue('');
  }, []);

  const saveFileName = React.useCallback(() => {
    if (!editingFileName || !editingFileNameValue.trim()) {
      // If no value, cancel editing
      cancelEditingFileName();
      return;
    }
    
    const newFileName = editingFileNameValue.trim();
    const file = files.find(f => f.id === editingFileName);
    
    if (!file) return;
    
    // Get the original extension
    const originalExtension = getFileExtension(file.type);
    
    // Ensure the new filename has the correct extension
    let finalFileName = newFileName;
    if (!finalFileName.endsWith(originalExtension)) {
      // Remove any existing extension and add the correct one
      const nameWithoutExt = finalFileName.split('.').slice(0, -1).join('.') || finalFileName;
      finalFileName = nameWithoutExt + originalExtension;
    }
    
    // Validate filename (basic validation)
    if (finalFileName === file.name) {
      cancelEditingFileName();
      return;
    }
    
    // Check if filename already exists
    const existingFile = files.find(f => f.name === finalFileName && f.id !== editingFileName);
    if (existingFile) {
      toast.warning('A file with this name already exists! Please choose a different name.');
      return;
    }
    
    // Update the file
    setFiles(prevFiles => 
      prevFiles.map(f => 
        f.id === editingFileName 
          ? { ...f, name: finalFileName, id: finalFileName }
          : f
      )
    );
    
    // Update active tab if it was the edited file
    if (activeTab === editingFileName) {
      setActiveTab(finalFileName);
    }
    
    cancelEditingFileName();
  }, [editingFileName, editingFileNameValue, files, getFileExtension, activeTab, cancelEditingFileName]);

  const handleFileNameKeyPress = React.useCallback((e) => {
    if (e.key === 'Enter') {
      saveFileName();
    } else if (e.key === 'Escape') {
      cancelEditingFileName();
    }
  }, [saveFileName, cancelEditingFileName]);



  // CSS: one reviewable test case per property (not one bundled rule)
  const buildCssPropertyDescription = React.useCallback((selector, property) => {
    return `Set property "${property}" for "${selector}"`;
  }, []);

  const expandCssTestCasesToPerProperty = React.useCallback((testCases) => {
    if (!Array.isArray(testCases)) return [];

    const expanded = [];
    testCases.forEach((tc) => {
      if (!tc || typeof tc !== 'object') return;

      const selector = tc.selector;
      const props = Array.isArray(tc.properties) ? tc.properties : [];

      // Non-CSS / malformed — keep as-is
      if (!selector || props.length === 0) {
        expanded.push(tc);
        return;
      }

      props.forEach((prop) => {
        const propertyName = (prop?.property || '').trim();
        if (!propertyName) return;

        const autoDescription = buildCssPropertyDescription(selector, propertyName);
        const existingDescription =
          typeof tc.description === 'string' ? tc.description.trim() : '';
        // Keep author edits only when already single-property and not the old bundled summary
        const keepDescription =
          props.length === 1 &&
          existingDescription &&
          !existingDescription.includes('(+') &&
          !/^Apply CSS for /i.test(existingDescription);

        expanded.push({
          selector,
          properties: [{ property: propertyName, value: prop.value }],
          description: keepDescription ? existingDescription : autoDescription,
        });
      });
    });

    return expanded;
  }, [buildCssPropertyDescription]);

  // Generate a stable signature for a test case to match across regenerations
  const makeTestCaseKey = React.useCallback((testCase) => {
    if (!testCase || typeof testCase !== 'object') return 'null';

    const normalizeAttributes = (attrs) => {
      if (!attrs || typeof attrs !== 'object') return {};
      const sorted = {};
      Object.keys(attrs).sort().forEach(k => {
        const v = attrs[k];
        if (Array.isArray(v)) {
          sorted[k] = [...v].sort();
        } else if (v && typeof v === 'object') {
          // Nested objects
          const nested = {};
          Object.keys(v).sort().forEach(nk => {
            nested[nk] = v[nk];
          });
          sorted[k] = nested;
        } else {
          sorted[k] = v;
        }
      });
      return sorted;
    };

    const normalizeProperties = (props) => {
      if (!Array.isArray(props)) return [];
      // sort by property name then value
      return [...props]
        .map(p => ({ property: p.property || '', value: p.value || '' }))
        .sort((a, b) => (a.property + ':' + a.value).localeCompare(b.property + ':' + b.value));
    };

    const signature = {
      // HTML
      tag: testCase.tag || '',
      parent: testCase.parent || '',
      content: testCase.content || '',
      attributes: normalizeAttributes(testCase.attributes || {}),
      // CSS
      selector: testCase.selector || '',
      properties: normalizeProperties(testCase.properties || []),
      type: testCase.type || '',
      description: testCase.description || '',
      variable: testCase.variable || '',
      expectedValue: testCase.hasOwnProperty('expectedValue') ? JSON.stringify(testCase.expectedValue) : '',
      functionName: testCase.functionName || '',
      expectedParameters: Array.isArray(testCase.expectedParameters) ? [...testCase.expectedParameters].sort() : [],
      expectedOutput: testCase.expectedOutput || '',
      event: testCase.event || '',
      method: testCase.method || '',
      loopType: testCase.loopType || ''
    };

    return JSON.stringify(signature);
  }, []);

  // Simple test case selection management (preserve by content signature, not index)
  const processTestCases = React.useCallback((newTestCases, fileId, isFirstGeneration = false) => {
    const previousList = generatedTestCases[fileId] || [];
    const existingSelections = selectedTestCases[fileId] || {};

    // Build maps from previous content signature -> selection
    const keyToSelection = new Map();
    previousList.forEach((oldTc, oldIdx) => {
      const key = makeTestCaseKey(oldTc);
      const oldKey = `${fileId}_${oldIdx}`;
      keyToSelection.set(key, !!existingSelections[oldKey]);
    });

    const newSelections = {};

    newTestCases.forEach((newTestCase, newIndex) => {
      const newKey = `${fileId}_${newIndex}`;
      if (isFirstGeneration) {
        // First generation after question load/new question: default unchecked
        newSelections[newKey] = false;
      } else {
        // Regeneration: preserve by matching signature if possible
        const signature = makeTestCaseKey(newTestCase);
        if (keyToSelection.has(signature)) {
          newSelections[newKey] = keyToSelection.get(signature);
        } else {
          newSelections[newKey] = false;
        }
      }
    });

    return { newSelections };
  }, [generatedTestCases, selectedTestCases, makeTestCaseKey]);

  // Generate JS test cases using backend API
  const generateJSTestcases = React.useCallback(async () => {
    const currentFile = getCurrentFileData;
    if (!currentFile || currentFile.type !== 'JS') {
      toast.warning('Please select a JavaScript file');
      return;
    }
    const code = currentFile.content || '';
    if (!code.trim()) {
      toast.warning('Please add JavaScript code first');
      return;
    }
    setIsGeneratingJSTestcases(true);
    setJsValidationResults([]);
    try {
      const testcaseGeneration = {};
      files.forEach(file => {
        if (file.name !== 'DefaultTemplate.html') {
          testcaseGeneration[file.name] = {
            code: file.content || ''
          };
        }
      });
      
      const response = await apiPostWithBaseURL('/generate-testcase?auto_validate=true', {
        testcase_generation: testcaseGeneration
      }, {}, config.API_JS_URL);

      if (response.status === 200) {
        const responseData = response.data;
        if (responseData.success && responseData.generated_testcases) {
          // Extract test cases for the current file from the generated_testcases object
          const fileTestCases = responseData.generated_testcases[currentFile.name];
          const newTestCases = fileTestCases?.testcases || [];
          
          if (newTestCases.length === 0) {
            toast.warning('No test cases generated for this file');
            setIsGeneratingJSTestcases(false);
            return;
          }
          
          const fileId = currentFile.id;
          const isFirstGeneration = !generatedTestCases[fileId] || generatedTestCases[fileId].length === 0;
          const existingTestCases = generatedTestCases[fileId];
          
          // Preserve edited and custom test cases
          const preservedTestCases = newTestCases.map((newTC, index) => {
            if (existingTestCases && existingTestCases[index]) {
              // Preserve edited test cases
              if (existingTestCases[index].isEdited) {
                return existingTestCases[index];
              }
              // Preserve custom test cases
              if (existingTestCases[index].custom_test_case) {
                return existingTestCases[index];
              }
            }
            return newTC;
          });
          
          // Add any additional custom test cases that weren't in the new generation
          if (existingTestCases && existingTestCases.length > newTestCases.length) {
            for (let i = newTestCases.length; i < existingTestCases.length; i++) {
              if (existingTestCases[i].custom_test_case || existingTestCases[i].isEdited) {
                preservedTestCases.push(existingTestCases[i]);
              }
            }
          }
          
          // Update generated test cases
          setGeneratedTestCases(prev => ({
            ...prev,
            [fileId]: preservedTestCases
          }));
          
          // Process test cases with diff logic
          const { newSelections } = processTestCases(
            newTestCases, 
            fileId, 
            isFirstGeneration
          );
          
          // Update selections
          setSelectedTestCases(prev => ({
            ...prev,
            [fileId]: newSelections
          }));
          
          if (onGenerateTestCases) {
            await onGenerateTestCases({
              selectedTestCases: {
                ...selectedTestCases,
                [fileId]: newSelections
              },
              generatedTestCases: {
                ...generatedTestCases,
                [fileId]: newTestCases
              }
            });
          }
          
          if (onTestCasesGenerated) {
            onTestCasesGenerated(currentFile.id);
          }
        } else if (responseData.success === false) {
          // Show error modal when success is false
          setShowJSTestCaseErrorModal(true);
        } else {
          throw new Error(responseData.message || 'Failed to generate test cases');
        }
      } else {
        throw new Error('Failed to generate test cases');
      }
    } catch (error) {
      toast.error('Error generating test cases: ' + error.message);
    } finally {
      setIsGeneratingJSTestcases(false);
    }
  }, [getCurrentFileData, files, generatedTestCases, selectedTestCases, onGenerateTestCases, onTestCasesGenerated, processTestCases]);

  const handleEditTestCase = React.useCallback((testCase, index) => {
    setEditingTestCase(testCase);
    setEditingTestCaseIndex(index);
    setIsCloning(false);
    setShowEditModal(true);
  }, []);

  const handleCloneTestCase = React.useCallback((testCase, index) => {
    setEditingTestCase(testCase);
    setEditingTestCaseIndex(null);
    setIsCloning(true);
    setShowEditModal(true);
  }, []);

  const handleCssDescriptionChange = React.useCallback((fileId, index, description) => {
    setGeneratedTestCases(prev => {
      const rawData = prev[fileId];
      if (!rawData) return prev;

      const isArray = Array.isArray(rawData);
      const list = isArray
        ? [...rawData]
        : [
            ...(Array.isArray(rawData.testcases)
              ? rawData.testcases
              : Array.isArray(rawData.test_cases)
                ? rawData.test_cases
                : Array.isArray(rawData.structure)
                  ? rawData.structure
                  : []),
          ];

      if (!list[index]) return prev;
      list[index] = { ...list[index], description };

      const nextForFile = isArray
        ? list
        : {
            ...rawData,
            ...(Array.isArray(rawData.testcases) ? { testcases: list } : {}),
            ...(Array.isArray(rawData.test_cases) ? { test_cases: list } : {}),
            ...(Array.isArray(rawData.structure) ? { structure: list } : {}),
            ...(!rawData.testcases && !rawData.test_cases && !rawData.structure
              ? { test_cases: list }
              : {}),
          };

      const updated = { ...prev, [fileId]: nextForFile };
      if (onTestCasesGenerated) {
        onTestCasesGenerated(updated);
      }
      return updated;
    });
  }, [onTestCasesGenerated]);

  const handleSaveEditedTestCase = React.useCallback((updatedTestCase) => {
    const rawData = generatedTestCases[activeTab];
    const testCases = Array.isArray(rawData) 
      ? rawData 
      : (rawData && (Array.isArray(rawData.testcases) 
        ? rawData.testcases 
        : (Array.isArray(rawData.test_cases) 
          ? rawData.test_cases 
          : (Array.isArray(rawData.structure) ? rawData.structure : [])))) || [];

    let updatedTestCases;

    if (isCloning) {

      const maxTestCaseNum = testCases.reduce((max, tc) => {
        const tcNum = tc.test_case || 0;
        return Math.max(max, tcNum);
      }, 0);
      
      const newTestCase = {
        ...updatedTestCase,
        custom_test_case: true,
        test_case: maxTestCaseNum + 1
      };
      delete newTestCase.isEdited;
      updatedTestCases = [...testCases, newTestCase];
    } else if (editingTestCaseIndex !== null && testCases[editingTestCaseIndex]) {
      // Editing existing test case
      updatedTestCases = [...testCases];
      updatedTestCases[editingTestCaseIndex] = {
        ...updatedTestCase,
        isEdited: true
      };
    } else {
      // Adding new custom test case
      const newTestCase = {
        ...updatedTestCase,
        custom_test_case: true,
        test_case: testCases.length + 1
      };
      updatedTestCases = [...testCases, newTestCase];
    }

    setGeneratedTestCases(prev => ({
      ...prev,
      [activeTab]: Array.isArray(rawData) 
        ? updatedTestCases 
        : { ...rawData, testcases: updatedTestCases, test_cases: updatedTestCases }
    }));

    // Auto-select the new/edited test case
    const testCaseIndex = isCloning ? updatedTestCases.length - 1 : (editingTestCaseIndex !== null ? editingTestCaseIndex : updatedTestCases.length - 1);
    setSelectedTestCases(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [`${activeTab}_${testCaseIndex}`]: true
      }
    }));

    if (onTestCasesGenerated) {
      const updated = {
        ...generatedTestCases,
        [activeTab]: Array.isArray(rawData) 
          ? updatedTestCases 
          : { ...rawData, testcases: updatedTestCases, test_cases: updatedTestCases }
      };
      onTestCasesGenerated(updated);
    }

    setShowEditModal(false);
    setEditingTestCase(null);
    setEditingTestCaseIndex(null);
    setIsCloning(false);
  }, [activeTab, editingTestCaseIndex, isCloning, generatedTestCases, onTestCasesGenerated]);

  const generateTestCasesForActiveFile = async () => {
    const currentFile = getCurrentFileData;
    
    if (!currentFile) {
      toast.warning('No file selected');
      return;
    }

    // Don't generate test cases for DefaultTemplate.html
    if (currentFile.name === 'DefaultTemplate.html') {
      toast.warning('Test cases cannot be generated for DefaultTemplate.html');
      return;
    }

    setIsGeneratingTestCases(true);
    
    try {
      const content = currentFile.content;
      const fileExtension = getFileExtension(currentFile.type);
      
      let apiEndpoint;
      if (fileExtension === '.html') {
        apiEndpoint = `${config.API_BASE_URL}/api/html-content/create/testcases/`;
      } else if (fileExtension === '.css') {
        apiEndpoint = `${config.API_BASE_URL}/api/css-content/create/testcases/`;
      } else {
        return;
      }

      const response = await apiPost(apiEndpoint.replace(config.API_BASE_URL, ''), {
        content: content
      });

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data;
        if (responseData.success && responseData.test_cases) {
          let newTestCases = responseData.test_cases;
          const fileId = currentFile.id;

          // Always expand CSS into one property = one test case for review
          if (fileExtension === '.css') {
            newTestCases = expandCssTestCasesToPerProperty(newTestCases);
          }
          
          // Check if this is the first generation for this file
          const isFirstGeneration = !generatedTestCases[fileId] || generatedTestCases[fileId].length === 0;
          
          // Update generated test cases
          setGeneratedTestCases(prev => ({
            ...prev,
            [fileId]: newTestCases
          }));
          
          // Process test cases with diff logic
          const { newSelections } = processTestCases(
            newTestCases, 
            fileId, 
            isFirstGeneration
          );
          
          // Update selections
          setSelectedTestCases(prev => ({
            ...prev,
            [fileId]: newSelections
          }));
          
          // Also call the parent's onGenerateTestCases if it exists
          if (onGenerateTestCases) {
            await onGenerateTestCases({
              selectedTestCases: {
                ...selectedTestCases,
                [fileId]: newSelections
              },
              generatedTestCases: {
                ...generatedTestCases,
                [fileId]: newTestCases
              }
            });
          }
          
          // Mark file as unmodified after test case generation
          if (onTestCasesGenerated) {
            onTestCasesGenerated(currentFile.id);
          }
        } else {
          throw new Error(responseData.message || 'Failed to generate test cases');
        }
      } else {
        throw new Error('Failed to generate test cases');
      }
    } catch (error) {
      toast.error('Error generating test cases: ' + error.message);
    } finally {
      setIsGeneratingTestCases(false);
    }
  };

  const handleTestCaseSelection = React.useCallback((fileId, testCaseIndex, isSelected) => {
    
    setSelectedTestCases(prev => {
      const newState = {
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [`${fileId}_${testCaseIndex}`]: isSelected
      }
  };
      return newState;
    });

  }, []); // Remove dependencies to prevent re-creation




  // File upload functions
  const handleFileUpload = async (file, type) => {
    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.warning(`File size must be less than 10MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    // Check file type
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allowedVideoTypes = ['video/mp4'];
    
    if (type === 'image' && !allowedImageTypes.includes(file.type)) {
      toast.warning('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    if (type === 'video' && !allowedVideoTypes.includes(file.type)) {
      toast.warning('Please select a valid video file (MP4 only)');
      return;
    }

    // Set appropriate loading state based on file type
    if (type === 'image') {
      setIsImageUploading(true);
    } else if (type === 'video') {
      setIsVideoUploading(true);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', sessionStorage.getItem('currentQuestionId')); // Get from sessionStorage
      formData.append('file_type', type);

      const response = await apiPostFormData('/api/content_creation/upload-content/', formData);

      const result = response.data;

      if (result.success) {
        if (type === 'image') {
          setUploadedImage(result.data);
          setImageCacheBust(Date.now());
          if (onImageUploaded) {
            onImageUploaded(result.data.url);
          }
        } else {
          setUploadedVideo(result.data);
          setVideoCacheBust(Date.now());
          if (onVideoUploaded) {
            onVideoUploaded(result.data.url);
          }
        }
        // Switch to Upload tab after successful upload
        setActivePreviewTab(CONSTANTS.UPLOAD_PREVIEW_TAB);
      } else {
        toast.error(`Upload failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Upload failed. Please try again.');
    } finally {
      // Reset appropriate loading state based on file type
      if (type === 'image') {
        setIsImageUploading(false);
      } else if (type === 'video') {
        setIsVideoUploading(false);
      }
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'image');
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'video');
    }
  };

  const removeUploadedFile = (type) => {
    if (type === 'image') {
      setUploadedImage(null);
    } else {
      setUploadedVideo(null);
    }
  };

  // Extracts the last path segment after the final slash, handling backslashes, trailing slashes, and query/hash
  const getLastPathSegment = (input) => {
    if (!input) return '';
    const withoutQuery = String(input).split('?')[0].split('#')[0];
    const normalized = withoutQuery.replace(/\\\\/g, '/').replace(/\\/g, '/');
    const trimmed = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
    const segments = trimmed.split('/');
    return segments[segments.length - 1] || '';
  };

  // Handle delete image/video - show confirmation modal
  const handleDeleteFile = (type) => {
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  // Confirm delete after modal confirmation
  const confirmDeleteFile = async () => {
    const fileData = deleteType === 'image' ? uploadedImage : uploadedVideo;
    if (!fileData) return;

    try {
      const formData = new FormData();
      formData.append('question_id', sessionStorage.getItem('currentQuestionId'));
      formData.append('file_type', deleteType);
      formData.append('filename', getLastPathSegment(fileData.filename || fileData.url));

      const response = await apiDelete('/api/content_creation/delete-content/', {
        data: formData
      });

      const result = response.data;

      if (result.success) {
        // Clear the uploaded file from state
        if (deleteType === 'image') {
          setUploadedImage(null);
          if (onImageUploaded) {
            onImageUploaded('');
          }
        } else {
          setUploadedVideo(null);
          if (onVideoUploaded) {
            onVideoUploaded('');
          }
        }
      } else {
        toast.error(`Delete failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Delete failed. Please try again.');
    } finally {
      // Close modal and reset state
      setShowDeleteModal(false);
      setDeleteType(null);
    }
  };

  // Cancel delete
  const cancelDeleteFile = () => {
    setShowDeleteModal(false);
    setDeleteType(null);
  };

  // Image URL management functions
  const addImageUrl = () => {
    setImageUrls(prev => [...prev, { actualUrl: '', expectedUrl: '' }]);
    setShowImageForm(true);
  };

  const removeImageUrl = (index) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const updateImageUrl = (index, field, value) => {
    setImageUrls(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };


  const validateImageUrls = () => {
    return imageUrls.every(item => item.actualUrl.trim() && item.expectedUrl.trim());
  };

  // Function to validate image URLs before save - can be called from parent component
  const validateImageUrlsForSave = () => {
    if (imageUrls.length === 0) {
      return { isValid: true, message: '' };
    }
    
    const incompleteImages = imageUrls.filter(item => !item.actualUrl.trim() || !item.expectedUrl.trim());
    
    if (incompleteImages.length > 0) {
      return {
        isValid: false,
        message: `Please fill both Actual and Expected URLs for all ${imageUrls.length} image(s). ${incompleteImages.length} image(s) are incomplete.`
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Handle reupload with previous URL
  const handleReupload = (type) => {
    const fileInput = document.getElementById(type === 'image' ? 'imageReupload' : 'videoReupload');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Handle reupload file change
  const handleReuploadChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const previousFile = type === 'image' ? uploadedImage : uploadedVideo;
      handleFileUploadWithPrevious(file, type, previousFile?.url);
    }
    // Reset the input value to allow re-uploading the same file
    event.target.value = '';
  };

  // Handle file upload with previous URL for replacement
  const handleFileUploadWithPrevious = async (file, type, previousUrl) => {
    if (type === 'image') {
      setIsImageUploading(true);
    } else if (type === 'video') {
      setIsVideoUploading(true);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', sessionStorage.getItem('currentQuestionId'));
      formData.append('file_type', type);
      if (previousUrl) {
        const previousFilename = getLastPathSegment(previousUrl);
        formData.append('filename', previousFilename);
      }

      const response = await apiPutFormData('/api/content_creation/update-content/', formData);

      const result = response.data;

      if (result.success) {
        if (type === 'image') {
          setUploadedImage(result.data);
          setImageCacheBust(Date.now());
          if (onImageUploaded) {
            onImageUploaded(result.data.url);
          }
        } else {
          setUploadedVideo(result.data);
          setVideoCacheBust(Date.now());
          if (onVideoUploaded) {
            onVideoUploaded(result.data.url);
          }
        }
        // Switch to Upload tab after successful upload
        setActivePreviewTab(CONSTANTS.UPLOAD_PREVIEW_TAB);
      } else {
        toast.error(`Reupload failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Reupload failed. Please try again.');
    } finally {
      // Reset appropriate loading state based on file type
      if (type === 'image') {
        setIsImageUploading(false);
      } else if (type === 'video') {
        setIsVideoUploading(false);
      }
    }
  };



  // Emit selected testcases to parent any time selections/results change (same as HTML/CSS)
  React.useEffect(() => {
    if (isLoadingFromAPI || isGeneratingJSTestcases) {
      return;
    }
    if (!onGenerateTestCases) return;

    const built = {};
    Object.entries(generatedTestCases || {}).forEach(([fileId, value]) => {
      const allCases = Array.isArray(value)
        ? value
        : (value && (Array.isArray(value.test_cases) ? value.test_cases : (Array.isArray(value.structure) ? value.structure : [])));
      const fileMeta = files.find(f => f.id === fileId) || files.find(f => f.name === fileId);
      const outKey = fileMeta?.name || fileId;
      built[outKey] = allCases;
    });

      const payload = {
        selectedTestCases,
      generatedTestCases: built
      };
      const payloadStr = JSON.stringify(payload);
      if (lastEmittedPayloadRef.current !== payloadStr) {
        lastEmittedPayloadRef.current = payloadStr;
        onGenerateTestCases(payload);
      }
  }, [selectedTestCases, generatedTestCases, onGenerateTestCases, isLoadingFromAPI, isGeneratingJSTestcases]);

  // Expose validation function to parent component
  React.useEffect(() => {
    if (onImageUrlsValidation) {
      onImageUrlsValidation(validateImageUrlsForSave);
    }
  }, [onImageUrlsValidation, imageUrls]);

  // Pass image URLs data to parent component
  React.useEffect(() => {
    if (onImageUrlsChange) {
      onImageUrlsChange(imageUrls);
    }
  }, [imageUrls, onImageUrlsChange]);



  const createPreviewHTML = React.useCallback((includeConsoleCapture = false) => {
    // Get the main HTML file (prefer index.html, fallback to first HTML file)
    const mainHtmlFile = files.find(f => f.name === 'index.html' && f.type === 'HTML') || 
                        files.find(f => f.type === 'HTML');
    
    if (!mainHtmlFile) {
      return '';
    }

    let htmlContent = mainHtmlFile.content || '';

    // If no HTML content, return empty
    if (!htmlContent.trim()) {
      return '';
    }

    // Ensure we have a complete HTML document
    if (!htmlContent.includes('<html')) {
      htmlContent = htmlContent;
    }
    if (!htmlContent.includes('<head')) {
      htmlContent = htmlContent.replace('<html>', '<html>\n<head>\n<title>Preview</title>\n</head>');
    }
    if (!htmlContent.includes('<body')) {
      htmlContent = htmlContent.replace('</head>', '</head>\n<body>') + '\n</body>';
    }

    // Add console capture script to the head only if requested
    if (includeConsoleCapture) {
      const consoleCaptureScript = `
        <script>
          (function() {
            // Ensure we only set up once per page load
            if (window.consoleCaptureSetup) return;
            window.consoleCaptureSetup = true;
            
            // Additional check to prevent multiple executions
            if (window.consoleCaptureInitialized) return;
            window.consoleCaptureInitialized = true;
            
            // Store original console methods
            const originalConsole = {
              log: console.log,
              warn: console.warn,
              error: console.error,
              info: console.info,
              debug: console.debug
            };
            
            function captureConsole(level, args) {
              try {
                const timestamp = new Date().toLocaleTimeString();
                const message = Array.from(args).map(arg => 
                  typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
                ).join(' ');
                
                // Send to parent window
                if (window.parent && window.parent !== window) {
                  window.parent.postMessage({
                    type: 'console',
                    level: level,
                    message: message,
                    timestamp: timestamp,
                    args: Array.from(args)
                  }, '*');
                }
              } catch (e) {
                // Fallback if JSON.stringify fails
                if (window.parent && window.parent !== window) {
                  window.parent.postMessage({
                    type: 'console',
                    level: level,
                    message: String(args),
                    timestamp: new Date().toLocaleTimeString(),
                    args: []
                  }, '*');
                }
              }
            }
            
            // Override console methods
            console.log = function(...args) {
              originalConsole.log.apply(console, args);
              captureConsole('log', args);
            };
            
            console.warn = function(...args) {
              originalConsole.warn.apply(console, args);
              captureConsole('warn', args);
            };
            
            console.error = function(...args) {
              originalConsole.error.apply(console, args);
              captureConsole('error', args);
            };
            
            console.info = function(...args) {
              originalConsole.info.apply(console, args);
              captureConsole('info', args);
            };
            
            console.debug = function(...args) {
              originalConsole.debug.apply(console, args);
              captureConsole('debug', args);
            };
          })();
        </script>
      `;
      
      // Insert console capture script into head
      if (htmlContent.includes('<head>')) {
        htmlContent = htmlContent.replace('<head>', '<head>' + consoleCaptureScript);
      } else {
        htmlContent = consoleCaptureScript + htmlContent;
      }
    }

    let htmlWithDataUrl = htmlContent;
    
    // Process all file references dynamically
    const processFileReferences = (pattern, fileType, dataUrlPrefix, attributeName = null) => {
      const matches = htmlWithDataUrl.match(pattern);
      if (matches) {
        matches.forEach(match => {
          // Extract filename from various attribute patterns
          const srcMatch = match.match(/src=["']([^"']+)["']|href=["']([^"']+)["']|data-src=["']([^"']+)["']|include=["']([^"']+)["']|data=["']([^"']+)["']/i);
          if (srcMatch) {
            const fileName = srcMatch[1] || srcMatch[2] || srcMatch[3] || srcMatch[4] || srcMatch[5];
            // Find the corresponding file content
            const referencedFile = files.find(f => f.name === fileName && f.type === fileType);
            if (referencedFile) {
              const fileContent = referencedFile.content || '';
              const fileDataUrl = `${dataUrlPrefix}${encodeURIComponent(fileContent)}`;
              
              // Determine which attribute to replace
              let attribute = attributeName;
              if (!attribute) {
                if (srcMatch[1]) attribute = 'src';
                else if (srcMatch[2]) attribute = 'href';
                else if (srcMatch[3]) attribute = 'data-src';
                else if (srcMatch[4]) attribute = 'include';
                else if (srcMatch[5]) attribute = 'data';
              }
              
              // Replace the reference
            htmlWithDataUrl = htmlWithDataUrl.replace(
                new RegExp(`${attribute}=["']${fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'),
                `${attribute}="${fileDataUrl}"`
            );
          }
        }
      });
    }
    };

    // Simple HTML file reference processor
    const processHTMLFileReferences = () => {
      // Find all HTML files in the files array
      const htmlFiles = files.filter(f => f.type === 'HTML');
      
      htmlFiles.forEach(htmlFile => {
        const fileName = htmlFile.name;
        const fileContent = htmlFile.content || '';
        const fileDataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(fileContent)}`;
        
        // Simple replacements - replace with actual content
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<div[^>]*include=["']${fileName}["'][^>]*></div>`, 'gi'),
          `<div>${fileContent}</div>`
        );
        
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<div[^>]*data-src=["']${fileName}["'][^>]*></div>`, 'gi'),
          `<div>${fileContent}</div>`
        );
        
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<link[^>]*rel=["']import["'][^>]*href=["']${fileName}["'][^>]*>`, 'gi'),
          `<div>${fileContent}</div>`
        );
        
        // For iframe, object, embed - replace with data URLs
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<iframe[^>]*src=["']${fileName}["'][^>]*>`, 'gi'),
          `<iframe src="${fileDataUrl}"`
        );
        
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<object[^>]*data=["']${fileName}["'][^>]*>`, 'gi'),
          `<object data="${fileDataUrl}"`
        );
        
        htmlWithDataUrl = htmlWithDataUrl.replace(
          new RegExp(`<embed[^>]*src=["']${fileName}["'][^>]*>`, 'gi'),
          `<embed src="${fileDataUrl}"`
        );
      });
    };

    // Process CSS files linked via <link> tags
    processFileReferences(
      /<link[^>]*rel=["']stylesheet["'][^>]*href=["']([^"']+)["'][^>]*>/gi,
      'CSS',
      'data:text/css;charset=utf-8,'
    );

    // Process JavaScript files linked via <script> tags
    const processJSFileReferences = () => {
      const scriptMatches = htmlWithDataUrl.match(/<script[^>]*src=["']([^"']+)["'][^>]*>/gi);
      
      if (scriptMatches) {
        scriptMatches.forEach(match => {
          const srcMatch = match.match(/src=["']([^"']+)["']/i);
          if (srcMatch) {
            const fileName = srcMatch[1];
            
            const referencedFile = files.find(f => f.name === fileName && f.type === 'JS');
            
            if (referencedFile) {
              const fileContent = referencedFile.content || '';
              const fileDataUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(fileContent)}`;
              
              // Replace the src attribute with data URL
              htmlWithDataUrl = htmlWithDataUrl.replace(
                new RegExp(`src=["']${fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'),
                `src="${fileDataUrl}"`
              );
            }
          }
        });
      }
    };
    
    processJSFileReferences();

    // Process HTML file references
    processHTMLFileReferences();

    // Process image URL replacements
    const processImageUrlReplacements = () => {
      if (imageUrls && imageUrls.length > 0) {
        imageUrls.forEach(imageUrl => {
          if (imageUrl.actualUrl && imageUrl.expectedUrl) {
            const patterns = [
              // src attributes
              new RegExp(`src=["']${imageUrl.expectedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'),
              // href attributes (for links)
              new RegExp(`href=["']${imageUrl.expectedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'),
              // data-src attributes
              new RegExp(`data-src=["']${imageUrl.expectedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']`, 'gi'),
              // background-image in style attributes
              new RegExp(`background-image:\\s*url\\(["']?${imageUrl.expectedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']?\\)`, 'gi'),
              // url() in CSS
              new RegExp(`url\\(["']?${imageUrl.expectedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']?\\)`, 'gi')
            ];

            patterns.forEach(pattern => {
              htmlWithDataUrl = htmlWithDataUrl.replace(pattern, (match) => {
                if (match.includes('src=')) {
                  return match.replace(imageUrl.expectedUrl, imageUrl.actualUrl);
                } else if (match.includes('href=')) {
                  return match.replace(imageUrl.expectedUrl, imageUrl.actualUrl);
                } else if (match.includes('data-src=')) {
                  return match.replace(imageUrl.expectedUrl, imageUrl.actualUrl);
                } else if (match.includes('background-image')) {
                  return match.replace(imageUrl.expectedUrl, imageUrl.actualUrl);
                } else if (match.includes('url(')) {
                  return match.replace(imageUrl.expectedUrl, imageUrl.actualUrl);
                }
                return match;
              });
            });
          }
        });
      }
    };

    processImageUrlReplacements();

    return htmlWithDataUrl;
  }, [files, imageUrls]);

  // Update preview when content changes
  React.useEffect(() => {
    const htmlContent = createPreviewHTML();
    if (htmlContent && htmlContent.trim()) {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } else {
      setPreviewUrl('');
    }
  }, [createPreviewHTML]);

  // Console-specific preview URL state
  const [consolePreviewUrl, setConsolePreviewUrl] = React.useState('');

  // Update console preview URL when content changes
  React.useEffect(() => {
    const htmlContent = createPreviewHTML(true); // Include console capture
    if (htmlContent && htmlContent.trim()) {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      setConsolePreviewUrl(url);
    } else {
      setConsolePreviewUrl('');
    }
  }, [createPreviewHTML]);

  // Clear console logs when switching tabs or when content changes significantly
  React.useEffect(() => {
    if (activePreviewTab === 'console') {
      // Don't clear console logs when just switching to console tab
      return;
    }
    // Clear console logs when switching away from console tab or when content changes
    setConsoleLogs([]);
  }, [activePreviewTab, createPreviewHTML]);

  // Listen for console messages from iframe
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'console') {
        // Add new console log to existing logs with duplicate prevention
        setConsoleLogs(prev => {
          const newLog = {
            id: Date.now() + Math.random(),
            level: event.data.level,
            message: event.data.message,
            timestamp: event.data.timestamp,
            args: event.data.args
          };
          
          // Check if this exact log already exists (prevent duplicates)
          const isDuplicate = prev.some(log => 
            log.message === newLog.message && 
            log.timestamp === newLog.timestamp &&
            log.level === newLog.level
          );
          
          if (isDuplicate) {
            return prev; // Don't add duplicate
          }
          
          return [...prev, newLog];
        });
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Clear console logs when switching questions or resetting
  React.useEffect(() => {
    setConsoleLogs([]);
  }, [questionId, resetTrigger]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);


  // Console tab refresh - simple approach
  React.useEffect(() => {
    if (activePreviewTab === 'console' && consolePreviewUrl) {
      // Clear console logs when switching to console tab
      setConsoleLogs([]);
      
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Force refresh by updating iframe key
      refreshTimeoutRef.current = setTimeout(() => {
        forceRefreshIframe();
      }, 100);
    }
  }, [activePreviewTab, consolePreviewUrl, forceRefreshIframe]);






  return (
    <div className="container-fluid p-0 m-0 bg-white mt-2" style={{ minHeight: '100vh' }}>
      <div className="row" style={{ minHeight: 'calc(100vh - 2rem)'}}>
        {/* Left Column - Form Controls and Test Cases */}
        <div className="col-lg-6 col-md-12" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 4rem)' }}>
          <div className="card border-0 h-100" style={{  borderRadius: '15px', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ backgroundColor: '#4f46e5', color: 'white', borderRadius: '15px 15px 0 0' }}>
              <h5 className="mb-0 fw-bold">Content Details</h5>
            </div>
            <div className="card-body p-0 pt-1" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* Form Collapse Button */}
        <div className="d-flex justify-content-between mb-2">
          <span className="fw-semibold text-dark">Task and Requirements</span>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => setIsFormCollapsed(!isFormCollapsed)}
            title={isFormCollapsed ? 'Expand form' : 'Collapse form'}
          >
            <FontAwesomeIcon 
              icon={isFormCollapsed ? faChevronDown : faChevronUp} 
              size="xs"
            />
          </button>
        </div>

        {/* Task (Qn) */}
        <div className="mb-4" style={{ display: isFormCollapsed ? 'none' : 'block' }}>
          <label htmlFor="pageName" className="form-label fw-semibold text-dark">
            Task: <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="pageName"
            className="form-control border-2"
            value={pageName}
            onChange={(e) => setPageName(e.target.value)}
            placeholder="Enter task (e.g., Simple Calculator)"
            required
            style={{ borderRadius: '10px', borderColor: '#e5e7eb' }}
          />
        </div>

        {/* Requirements */}
        <div className="mb-4" style={{ display: isFormCollapsed ? 'none' : 'block' }}>
          <label htmlFor="question" className="form-label fw-semibold text-dark">
           Requirements: <span className="text-danger">*</span>
          </label>
          <QuestionEditor
            resetKey={questionId || 'html-requirements'}
            value={requirementsSeed}
            onChange={handleQuestionChange}
            placeholder="Enter the requirements with detailed instructions"
          />
            <div className="d-flex justify-content-between align-items-center my-3">
               <span>Image URLs:</span>
               <button
                 className='btn btn-outline-primary btn-sm'
                 onClick={addImageUrl}
                 title="Add Image URLs"
               >
               <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />
               Image
               </button>
            </div> 
        </div>

        {/* Image URLs Section */}
        {imageUrls.length > 0 && (
          <div className="mb-4" style={{ display: isFormCollapsed ? 'none' : 'block' }}>
            <div className="border rounded p-3" style={{ backgroundColor: '#f9fafb' }}>
              {imageUrls.map((imageUrl, index) => (
                <div key={index} className="mb-3 p-3 border rounded" style={{ backgroundColor: 'white' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 fw-semibold">Image{index + 1}</h6>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeImageUrl(index)}
                      title="Remove this image"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label className="form-label small fw-semibold">Actual Image URL:</label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        value={imageUrl.actualUrl}
                        onChange={(e) => updateImageUrl(index, 'actualUrl', e.target.value)}
                        placeholder="Enter actual image URL"
                        style={{ borderRadius: '8px' }}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label small fw-semibold">Name of Image URL:</label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        value={imageUrl.expectedUrl}
                        onChange={(e) => updateImageUrl(index, 'expectedUrl', e.target.value)}
                        placeholder="Enter name of image URL"
                        style={{ borderRadius: '8px' }}
                      />
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
          </div>
        )}




        {/* Test Cases Section - HTML/CSS/JS files */}
        {generatedTestCases[activeTab] && activeTab !== 'DefaultTemplate.html' && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <label className="form-label fw-semibold text-dark mb-0">
                Testcase  <button className='btn btn-secondary btn-sm'>{getCurrentFileData?.name}</button>
              </label>
              <div>
                {getCurrentFileData?.type === 'JS' && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm fw-semibold me-2"
                    onClick={() => {
                      setEditingTestCase(null);
                      setEditingTestCaseIndex(null);
                      setShowEditModal(true);
                    }}
                    style={{ borderRadius: '8px' }}
                    title="Add custom test case"
                  >
                    <i className="fa fa-plus"></i> Add Custom Test Case
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm fw-semibold"
                  onClick={() => {
                    const rawData = generatedTestCases[activeTab];
                    const testCases = Array.isArray(rawData) ? rawData : (rawData && (Array.isArray(rawData.testcases) ? rawData.testcases : (Array.isArray(rawData.test_cases) ? rawData.test_cases : (Array.isArray(rawData.structure) ? rawData.structure : []))));
                    const allSelected = testCases.every((_, index) => 
                      selectedTestCases[activeTab]?.[`${activeTab}_${index}`] || false
                    );
                    const newSelections = {};
                    testCases.forEach((_, index) => {
                      newSelections[`${activeTab}_${index}`] = !allSelected;
                    });
                    
                    setSelectedTestCases(prev => ({
                      ...prev,
                      [activeTab]: newSelections
                    }));
                  }}
                  style={{ borderRadius: '8px' }}
                >
                  {(() => {
                    const rawData = generatedTestCases[activeTab];
                    const testCases = Array.isArray(rawData) ? rawData : (rawData && (Array.isArray(rawData.testcases) ? rawData.testcases : (Array.isArray(rawData.test_cases) ? rawData.test_cases : (Array.isArray(rawData.structure) ? rawData.structure : []))));
                    const allSelected = testCases.every((_, index) => 
                      selectedTestCases[activeTab]?.[`${activeTab}_${index}`] || false
                    );
                    return allSelected ? 'Deselect All' : 'Select All';
                  })()}
                </button>
              </div>
            </div>
            <div className="test-cases-list" style={{ 
              maxHeight: '600px', 
              overflowY: 'auto', 
              border: '2px solid #e5e7eb', 
              borderRadius: '10px', 
              padding: '15px',
              backgroundColor: '#f9fafb'
            }}>
              {(() => {
                 const rawData = generatedTestCases[activeTab];
                 const testCases = Array.isArray(rawData) ? rawData : (rawData && (Array.isArray(rawData.test_cases) ? rawData.test_cases : (Array.isArray(rawData.structure) ? rawData.structure : [])));
                
                if (Array.isArray(testCases) && testCases.length > 0) {
                  return testCases.map((testCase, index) => {
                    return (
                <div key={index} className="test-case-item mb-3 p-3 border-0 rounded-3 shadow-sm" style={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={selectedTestCases[activeTab]?.[`${activeTab}_${index}`] || false}
                        onChange={(e) => handleTestCaseSelection(activeTab, index, e.target.checked)}
                      />
                      <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                        {testCase.description
                          ? `Test Case ${index + 1}: ${testCase.description}`
                          : `Test Case ${index + 1}`}
                      </h6>
                      {(() => {
                        const validationResult = jsValidationResults.find(r => r.index === index);
                        if (validationResult) {
                          return (
                            <span style={{ marginLeft: '10px' }}>
                              {validationResult.pass ? 
                                <i className="fa fa-check-circle" style={{ color: '#10b981', fontSize: '16px' }}></i> : 
                                <i className="fa fa-times-circle" style={{ color: '#ef4444', fontSize: '16px' }}></i>
                              }
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    {getCurrentFileData?.type === 'JS' && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditTestCase(testCase, index)}
                          title={testCase.isEdited ? "Edit test case again" : "Edit test case"}
                        >
                          <i className="fa fa-edit"></i> {testCase.isEdited ? 'Edit Again' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleCloneTestCase(testCase, index)}
                          title="Clone test case"
                        >
                          <i className="fa fa-clone"></i> Clone
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="test-case-details">
                    {getCurrentFileData?.type !== 'JS' && testCase.tag && (
                      <>
                        <div className="">
                          <strong style={{ fontSize: '13px', color: '#374151' }}>Tag:</strong>
                          <span style={{ fontSize: '13px', marginLeft: '8px', color: '#6b7280' }}>{testCase.tag}</span>
                        </div>
                        
                        {/* Show content only if it exists */}
                        {testCase.content && (
                          <div className="">
                            <strong style={{ fontSize: '13px', color: '#374151' }}>Content:</strong>
                            <span style={{ fontSize: '13px', marginLeft: '8px', color: '#6b7280' }}>{testCase.content}</span>
                          </div>
                        )}
                      
                        
                        {/* Show attributes only if they exist */}
                        {testCase.attributes && Object.keys(testCase.attributes).length > 0 && (
                          <div className="">
                            <strong style={{ fontSize: '13px', color: '#374151' }}>Attributes:</strong>
                            <div style={{ marginLeft: '50px', marginTop: '4px' }}>
                              {Object.entries(testCase.attributes).map(([key, value]) => (
                                <div key={key} style={{ fontSize: '13px', marginBottom: '2px' }}>
                                  <strong style={{ color: '#374151' }}>{key}:</strong>
                                  <span style={{ color: '#6b7280', marginLeft: '4px' }}>
                                    {Array.isArray(value) ? value.join(', ') : value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {getCurrentFileData?.type !== 'JS' && testCase.selector && (
                      <>
                        <div className="mb-2">
                          <strong style={{ fontSize: '13px', color: '#374151' }}>Description:</strong>
                          <textarea
                            className="form-control form-control-sm mt-1"
                            rows={2}
                            value={testCase.description || ''}
                            placeholder='e.g. Apply CSS for ".card": margin, color'
                            onChange={(e) =>
                              handleCssDescriptionChange(
                                getCurrentFileData.id,
                                index,
                                e.target.value
                              )
                            }
                            style={{ fontSize: '13px' }}
                          />
                        </div>
                        <div className="">
                          <strong style={{ fontSize: '13px', color: '#374151' }}>Selector:</strong>
                          <span style={{ fontSize: '13px', marginLeft: '8px', color: '#6b7280' }}>{testCase.selector}</span>
                        </div>
                        
                        {/* Show properties only if they exist */}
                        {testCase.properties && testCase.properties.length > 0 && (
                          <div className="">
                            <strong style={{ fontSize: '13px', color: '#374151' }}>Properties:</strong>
                            <div style={{ marginLeft: '8px', marginTop: '4px' }}>
                              {testCase.properties.map((prop, propIndex) => (
                                <div key={propIndex} style={{ fontSize: '13px', marginBottom: '2px' }}>
                                  <strong style={{ color: '#374151' }}>{prop.property}:</strong>
                                  <span style={{ color: '#6b7280', marginLeft: '4px' }}>
                                    {prop.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {(getCurrentFileData?.type === 'JS') && (
                      <div className="mt-2">
                        {(() => {
                          const validationResult = jsValidationResults.find(r => r.index === index);
                          const displayType = validationResult?.type || testCase.type;
                          const displayDescription = testCase.description || validationResult?.description;
                          
                          return (
                            <>
                              {displayType && (
                                <div>
                                  <strong style={{ fontSize: '13px', color: '#374151' }}>Type:</strong>
                                  <span className="badge bg-secondary ms-2" style={{ fontSize: '11px' }}>{displayType}</span>
                                </div>
                              )}
                              {displayDescription && (
                                <div>
                                  <strong style={{ fontSize: '13px', color: '#374151' }}>Description:</strong>
                                  <span style={{ fontSize: '13px', marginLeft: '8px', color: '#6b7280' }}>{displayDescription}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                    </div>
                    );
                  });
                } else {
                  return (
                    <div className="text-center p-4 text-muted">
                      <p>No test cases available for this file.</p>
                      <small>Click "Generate Test Cases" to create test cases for this file.</small>
                </div>
                  );
                }
              })()}
            </div>
          </div>
        )}


        {/* JS form removed: JS uses the same left-side testcases flow as HTML/CSS */}
            </div>
          </div>
        </div>

        {/* Right Column - Code Editor and Live Preview */}
        <div className="col-lg-6 col-md-12" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 4rem)' }}>
          <div className="card border-0 h-100" style={{ borderRadius: '15px', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ backgroundColor: '#059669', color: 'white', borderRadius: '15px 15px 0 0' }}>
              <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold">Code Editor & Preview</h5>
            </div>
            </div>
            <div className="card-body p-0" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* File Management */}
          <div className="pt-1 border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <label className="form-label mb-0 fw-semibold text-dark">Files:</label>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm fw-semibold me-2"
                  onClick={() => addNewFile('HTML')}
                  style={{ borderRadius: '8px' }}
                >
                  + HTML
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm fw-semibold me-2"
                  onClick={() => addNewFile('CSS')}
                  style={{ borderRadius: '8px' }}
                >
                  + CSS
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info btn-sm fw-semibold me-2"
                  onClick={() => addNewFile('JS')}
                  style={{ borderRadius: '8px' }}
                >
                  + JS
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm fw-semibold me-2"
                  onClick={addDefaultTemplate}
                  disabled={hasDefaultTemplate}
                  style={{ borderRadius: '8px' }}
                >
                  + Template
                </button>
              </div>
            </div>

            {/* File Tabs */}
            <div className="nav nav-tabs border-0" role="tablist" style={{ backgroundColor: '#f8f9fa' }}>
              {allFiles.map((file) => (
                <div key={file.id} className="nav-item d-flex align-items-center">
                  {editingFileName === file.id ? (
                    <div className="d-flex align-items-center">
                      <div className="input-group input-group-sm" style={{ width: '140px' }}>
                        <input
                          type="text"
                          value={editingFileNameValue}
                          onChange={(e) => setEditingFileNameValue(e.target.value)}
                          onKeyDown={handleFileNameKeyPress}
                          className="form-control"
                          placeholder={`filename${getFileExtension(file.type)}`}
                          autoFocus
                        />
                        <span className="input-group-text">{getFileExtension(file.type)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-success btn-sm ms-1"
                        onClick={saveFileName}
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm ms-1"
                        onClick={cancelEditingFileName}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      className={`nav-link ${activeTab === file.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(file.id)}
                      onDoubleClick={() => startEditingFileName(file.id, file.name)}
                      type="button"
                      title="Double-click to edit filename"
                      style={activeTab === file.id ? { color: '#007bff', fontWeight: 'bold' } : { color: '#000000' }}
                    >
                      {file.name}
                      {(file.id !== 'index.html' && (files.length > 1 || file.id === 'DefaultTemplate.html')) && (
                        <button
                          type="button"
                          className="btn-close btn-close-sm ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(file.id);
                          }}
                          aria-label="Remove file"
                        />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="card-body p-0" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              {(() => {
                const currentFile = getCurrentFileData;
                return currentFile?.type ? (
                  <div style={{ height: '400px', overflowY: 'auto' }}>
                  <CodeMirror
                      key={`code-editor-${currentFile.id}`}
                      value={getCurrentFileContent}
                    onChange={updateCurrentFileContent}
                    extensions={[
                      getLanguage(currentFile.type),
                      ...(getLinter(currentFile.type) || []),
                      lintGutter()
                    ]}
                    theme={oneDark}
                    height="400px"
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: true,
                      dropCursor: false,
                      allowMultipleSelections: false,
                      indentOnInput: true,
                      bracketMatching: true,
                      closeBrackets: true,
                      autocompletion: true,
                      highlightSelectionMatches: false
                    }}
                  />
                  </div>
                ) : (
                  <div className="text-center p-4" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p>No file selected or file type not supported</p>
                  </div>
                );
              })()}

        {/* Tab Navigation */}
        <div className="d-flex justify-content-between align-items-end py-0" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e2e8f0' }}>
          <div className="nav nav-tabs border-0" role="tablist" style={{ marginBottom: '-1px' }}>
            <button
              className={`nav-link ${activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB ? 'active' : ''}`}
              onClick={() => setActivePreviewTab(CONSTANTS.DEFAULT_PREVIEW_TAB)}
              type="button"
              style={{ 
                backgroundColor: activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB ? '#fff' : 'transparent',
                border: '1px solid transparent',
                borderBottom: activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB ? '1px solid #fff' : '1px solid #e2e8f0',
                borderRadius: '8px 8px 0 0',
                padding: '12px 20px',
                fontWeight: '600',
                color: activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB ? '#007bff' : '#6c757d',
                marginRight: '2px',
                position: 'relative',
                zIndex: activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB ? '2' : '1'
              }}
            >
              Output
            </button>
            <button
              className={`nav-link ${activePreviewTab === 'console' ? 'active' : ''}`}
              onClick={() => setActivePreviewTab('console')}
              type="button"
              style={{ 
                backgroundColor: activePreviewTab === 'console' ? '#fff' : 'transparent',
                border: '1px solid transparent',
                borderBottom: activePreviewTab === 'console' ? '1px solid #fff' : '1px solid #e2e8f0',
                borderRadius: '8px 8px 0 0',
                padding: '12px 20px',
                fontWeight: '600',
                color: activePreviewTab === 'console' ? '#007bff' : '#6c757d',
                marginRight: '2px',
                position: 'relative',
                zIndex: activePreviewTab === 'console' ? '2' : '1'
              }}
            >
              Console
            </button>
              <FontAwesomeIcon className="p-3" icon={faExpand} title="Full Screen Preview" onClick={() => setShowPreviewModal(true)}/>
            <button
              className={`nav-link ${activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB ? 'active' : ''}`}
              onClick={() => setActivePreviewTab(CONSTANTS.UPLOAD_PREVIEW_TAB)}
              type="button"
              style={{ 
                backgroundColor: activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB ? '#fff' : 'transparent',
                border: '1px solid transparent',
                borderBottom: activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB ? '1px solid #fff' : '1px solid #e2e8f0',
                borderRadius: '8px 8px 0 0',
                padding: '12px 20px',
                fontWeight: '600',
                color: activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB ? '#007bff' : '#6c757d',
                marginRight: '2px',
                position: 'relative',
                zIndex: activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB ? '2' : '1'
              }}
            >
              Upload
            </button>
          </div>
          
          {/* Right side button - Unified for HTML/CSS/JS */}
          <div className="p-2">
            <div className="btn-group" role="group">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm fw-semibold"
                onClick={() => {
                if (getCurrentFileData?.type === 'JS') {
                  generateJSTestcases();
                } else {
                  generateTestCasesForActiveFile();
                }
              }}
              disabled={isGeneratingTestCases || isGeneratingJSTestcases}
              title="Generate test cases for active file"
              style={{ borderRadius: '8px' }}
            >
              {(isGeneratingTestCases || isGeneratingJSTestcases) ? 'Generating...' : 'Generate Test Cases'}
            </button>
          {getCurrentFileData?.type === 'JS' && (
              <button
                type="button"
                className="btn btn-outline-success btn-sm fw-semibold ms-2"
                onClick={async () => {
                  setIsValidating(true);
                  setJsValidationResults([]);
                  try {
                  const currentFile = getCurrentFileData;
                  const code = currentFile?.content || '';
                  const allCases = Array.isArray(generatedTestCases[currentFile?.id]) ? generatedTestCases[currentFile.id] : [];
                  const selections = selectedTestCases?.[currentFile?.id] || {};
                    
                    // Track original indices of selected test cases
                  const selectedIndices = [];
                  const selected = allCases.filter((_, idx) => {
                  const isSelected = !!selections[`${currentFile?.id}_${idx}`];
                    if (isSelected) {
                      selectedIndices.push(idx);
                    }
                    return isSelected;
                  });                  
                  const tests = selected.length > 0 ? selected : allCases;
                  const testIndices = selected.length > 0 ? selectedIndices : allCases.map((_, idx) => idx);

                  // Supply all open files as Code_Validation-style blocks
                  const codeValidation = {};
                  (files || []).forEach(f => {
                    const name = f?.name || f?.id;
                    if (!name) return;
                    codeValidation[name] = { Ans: f?.content || '' };
                  });
                  // Include current file testcases (selected preferred)
                  if (currentFile && (currentFile.name || currentFile.id)) {
                    const key = currentFile.name || currentFile.id;
                    if (!codeValidation[key]) codeValidation[key] = { Ans: code };
                    codeValidation[key].testcases = tests;
                  }
                  const payload = { generated_testcases: codeValidation };
                  try {
                    const res = await apiPostWithBaseURL('/validate', payload, {}, config.API_JS_URL);
                    const submitData = res.data;
                    
                    if (!submitData?.submission_id) {
                      setJsValidationResults([{ 
                        index: 0, 
                        type: 'error', 
                        description: 'Failed to submit validation request', 
                        pass: false, 
                        details: {} 
                      }]);
                      return;
                    }

                    const submissionId = submitData.submission_id;
                    const estimatedWaitTime = submitData.estimated_wait_time || 0;
                    const initialWaitTime = Math.max(estimatedWaitTime - 2, 0) * 1000; 
                    if (initialWaitTime > 0) {
                      await new Promise(resolve => setTimeout(resolve, initialWaitTime));
                    }
                    
                    const pollForResults = async () => {
                      const maxAttempts = 60; // Max 60 seconds
                      let attempts = 0;
                      
                      while (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        
                        try {
                          const statusRes = await apiGetWithBaseURL(`/api/v1/status/${submissionId}`, {}, {}, config.API_JS_URL);
                          const statusData = statusRes.data;
                          
                          if (statusData.status === 'completed') {
                            // Process completed results
                            let testsJson = [];
                            if (statusData?.result?.result?.parsed_results) {
                              testsJson = statusData.result.result.parsed_results;
                            } else if (statusData?.result?.parsed_results) {
                              testsJson = statusData.result.parsed_results;
                            } else if (statusData?.report?.tests) {
                              testsJson = statusData.report.tests;
                            }
                            
                            const mapped = testsJson.map((t, idx) => ({ 
                              index: testIndices[idx], // Use original index from allCases
                              type: t.type || t.category || '', 
                              description: t.title || t.fullTitle || '', 
                              pass: t.passed !== undefined ? t.passed : (t.state === 'passed'), 
                              details: t.err || {} 
                            }));
                            setJsValidationResults(mapped);
                            return;
                          } else if (statusData.status === 'running' || statusData.status === 'pending') {
                            attempts++;
                            continue;
                          } else {
                            setJsValidationResults([{ 
                              index: 0, 
                              type: 'status', 
                              description: `Validation status: ${statusData.status}`, 
                              pass: false, 
                              details: statusData 
                            }]);
                            return;
                          }
                        } catch (pollError) {
                          attempts++;
                        }
                      }
                      setJsValidationResults([{ 
                        index: 0, 
                        type: 'error', 
                        description: 'Validation timeout - please try again', 
                        pass: false, 
                        details: {} 
                      }]);
                    };
                    
                    await pollForResults();
                  } catch (e) {
                    setJsValidationResults([{ 
                      index: 0, 
                      type: 'error', 
                      description: `Validation error: ${e.message}`, 
                      pass: false, 
                      details: {} 
                    }]);
                  }
                 } finally {
                    setIsValidating(false);
                  }
                }}
                disabled={isValidating}
                title="Validate JS against generated test cases"
                style={{ borderRadius: '8px' }}
              >
                {isValidating ? 'Validating...' : 'Validate'}
              </button>
          )}
            </div>
            </div>
          
         
        </div>

        {/* Tab Content */}
        {activePreviewTab === CONSTANTS.DEFAULT_PREVIEW_TAB && (
          <div style={{ position: 'relative', flex: 1, minHeight: '300px' }}>
            <iframe
              ref={iframeRef}
              key={`preview-${previewUrl}-${iframeKey}`}
              src={previewUrl}
              title="Live Preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '0 0 15px 15px',
                background: 'white',
                display: 'block',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
              }}
              sandbox="allow-scripts allow-same-origin"
              loading="lazy"
              onError={(e) => {
                // Show fallback content if iframe fails
                const iframeContainer = e.target.parentElement;
                const htmlContent = createPreviewHTML();
                iframeContainer.innerHTML = `
                  <div style="padding: 20px; background: #f8f9fa; height: 100%; overflow: auto;">
                    <h6>Preview (Fallback Mode)</h6>
                    <pre style="white-space: pre-wrap; font-size: 12px;">${htmlContent}</pre>
                  </div>
                `;
              }}
            />
          </div>
        )}

        {activePreviewTab === 'console' && (
          <div style={{ position: 'relative', flex: 1, minHeight: '300px', display: 'flex', flexDirection: 'column' }}>
            {/* Hidden iframe for console capture - only when in console tab */}
            <iframe
              ref={iframeRef}
              key={`console-${iframeKey}`}
              src={consolePreviewUrl}
              title="Console Preview"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: 0,
                pointerEvents: 'none',
                zIndex: -1
              }}
              sandbox="allow-scripts allow-same-origin"
            />
            
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
              <h6 className="mb-0 fw-semibold">Console Output</h6>
            </div>
            <div 
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                backgroundColor: '#1e1e1e', 
                color: '#d4d4d4',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '13px',
                padding: '10px'
              }}
            >
              {consoleLogs.length === 0 ? (
                <div className="text-center text-muted p-4">
                  <p className="mb-0">No console output yet</p>
                  <small>Console logs from your HTML/JavaScript code will appear here</small>
                </div>
              ) : (
                consoleLogs.map((log) => (
                  <div key={log.id} className="mb-2 d-flex align-items-start">
                    <span style={{ wordBreak: 'break-word' }}>
                      {log.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activePreviewTab === CONSTANTS.UPLOAD_PREVIEW_TAB && (
          <div className="p-4" style={{ flex: 1, minHeight: '300px' }}>
            <div className="row">
              {/* Image Upload */}
              <div className="col-md-6 mb-3">
                <div className="card border-2" style={{ borderRadius: '10px', borderColor: '#e5e7eb' }}>
                  <div className="card-body text-center">
                    <h6 className="card-title">📷 Image Upload</h6>
                    
                    {uploadedImage ? (
                      <div className="mb-3">
                        <img 
                          src={withCacheBust(uploadedImage.url, imageCacheBust)} 
                          alt="Uploaded" 
                          style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px' }}
                        />
                        <div className="mt-2">
                          <small className="text-muted">{uploadedImage.filename}</small>
                        </div>
                        <div className="mt-3 d-flex gap-2 justify-content-center">
                          <input
                            type="file"
                            id="imageReupload"
                            accept="image/*"
                            onChange={(e) => handleReuploadChange(e, 'image')}
                            disabled={isImageUploading}
                            style={{ display: 'none' }}
                          />
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleReupload('image')}
                            disabled={isImageUploading}
                          >
                            {isImageUploading ? 'Reuploading...' : 'Reupload'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteFile('image')}
                            disabled={isImageUploading}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          id="imageUpload"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={!isQuestionSaved || isImageUploading}
                          style={{ display: 'none' }}
                        />
                        <label 
                          htmlFor="imageUpload" 
                          className={`btn ${isQuestionSaved && !isImageUploading ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ cursor: isQuestionSaved && !isImageUploading ? 'pointer' : 'not-allowed' }}
                        >
                          {isImageUploading ? 'Uploading...' : 'Choose Image'}
                        </label>
                        {!isQuestionSaved && (
                          <p className="text-warning small mt-2">Please save the question first</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Video Upload */}
              <div className="col-md-6 mb-3">
                <div className="card border-2" style={{ borderRadius: '10px', borderColor: '#e5e7eb' }}>
                  <div className="card-body text-center">
                    <h6 className="card-title">🎥 Video Upload</h6>
                    
                    {uploadedVideo ? (
                      <div className="mb-3">
                        <video 
                          src={withCacheBust(uploadedVideo.url, videoCacheBust)} 
                          controls 
                          style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px' }}
                        />
                        <div className="mt-2">
                          <small className="text-muted">{uploadedVideo.filename}</small>
                        </div>
                        <div className="mt-3 d-flex gap-2 justify-content-center">
                          <input
                            type="file"
                            id="videoReupload"
                            accept="video/mp4"
                            onChange={(e) => handleReuploadChange(e, 'video')}
                            disabled={isVideoUploading}
                            style={{ display: 'none' }}
                          />
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleReupload('video')}
                            disabled={isVideoUploading}
                          >
                            {isVideoUploading ? 'Reuploading...' : 'Reupload'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteFile('video')}
                            disabled={isVideoUploading}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          id="videoUpload"
                          accept="video/mp4"
                          onChange={handleVideoUpload}
                          disabled={!isQuestionSaved || isVideoUploading}
                          style={{ display: 'none' }}
                        />
                        <label 
                          htmlFor="videoUpload" 
                          className={`btn ${isQuestionSaved && !isVideoUploading ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ cursor: isQuestionSaved && !isVideoUploading ? 'pointer' : 'not-allowed' }}
                        >
                          {isVideoUploading ? 'Uploading...' : 'Choose Video'}
                        </label>
                        {!isQuestionSaved && (
                          <p className="text-warning small mt-2">Please save the question first</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
            </div>
          </div>

        </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div 
          className="modal show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowPreviewModal(false)}
        >
          <div 
            className="modal-dialog modal-fullscreen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Output - Full Screen</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreviewModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-0">
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    title="Live Preview - Full Screen"
                    style={{
                      width: '100%',
                      height: 'calc(100vh - 120px)',
                      border: 'none',
                      background: 'white'
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    loading="lazy"
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center" style={{ height: 'calc(100vh - 120px)' }}>
                    <div className="text-center text-muted">
                      <p>No HTML content to preview</p>
                      <small>Add content to index.html to see live preview</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          className="modal show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={cancelDeleteFile}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title fw-bold">
                  {deleteType === 'image' ? '🗑️ Delete Image' : '🗑️ Delete Video'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelDeleteFile}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  {deleteType === 'image' 
                    ? 'Are you sure you want to delete this image?' 
                    : 'Are you sure you want to delete this video?'
                  }
                </p>
                <small className="text-muted">This action cannot be undone.</small>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDeleteFile}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteFile}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={showJSTestCaseErrorModal}
        onHide={() => setShowJSTestCaseErrorModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Unable to Generate Test Cases</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Unable to generate testcase for your JS code, please check your code
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowJSTestCaseErrorModal(false)}
          >
            OK
          </button>
        </Modal.Footer>
      </Modal>

      <TestCaseEditModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingTestCase(null);
          setEditingTestCaseIndex(null);
          setIsCloning(false);
        }}
        testCase={editingTestCase}
        onSave={handleSaveEditedTestCase}
        fileName={activeTab}
        isCloning={isCloning}
        jsCode={(() => {
          // Find JS file content based on fileName
          const jsFile = files.find(f => (f.name === activeTab || f.id === activeTab) && f.type === 'JS');
          return jsFile?.content || '';
        })()}
      />
    </div>
  );
};

export default HTMLForm;